import { Resolvers } from "@apollo/client";
import { getProvider } from "@wagmi/core";
import { BigNumber } from "ethers";

import { BORROWER_METADATA, POOL_METADATA } from "@/constants";
import { getContract } from "@/lib/contracts";

import { LoanDelinquency, TranchedPool } from "../generated";
import {
  collectedPaymentBalance,
  interestOwed,
  isAfterTermEndTime,
} from "./credit-line";

const oldCreditLines = [
  "0x6e31500fc5798c9d817adbf5f9f8f78b12a48dcc",
  "0x2d1d3bb16c582ff0cc0ba20f2bfe36523ec52c42",
  "0xe1c38141fe108776446785b8befdb75296a37d79",
  "0xe1c38141fe108776446785b8befdb75296a37d79",
  "0x491dd8c5915be2eb18102669da7f8ce1e16481d6",
  "0xc79e33aa41760fa5c0f0239ce8dff31c891baad6",
  "0x0099f9b99956a495e6c59d9105193ea46abe2d56",
  "0x1319e68716dbfc06b4cdb031700f6bcd03729e52",
  "0x68c000c96aa06798fb921424cad25479c9a40c5f",
  "0x1eddfaeb7663702f18a74f4dbe685368553e20e5",
  "0x2abefac70de136bb385f8e2e46d6f64da7da664a",
  "0x0268fda3296dbf12d2f918967cbe6af9ae9c4df8",
  "0x7666de84357db649d973232834d6456af3fa61bc",
  "0x70bfe90aa06d0a13d7b784d5fc258a4e371ad58f",
  "0xdb82e4f42f0751550be6d4fb475ad99d219534ac",
  "0xfb683afd92b44b685c57a541ce85720e68316d3c",
  "0xd7f944dbf24309d700213a79775466a3fd809784",
  "0xa6169c2638735ff5627c2e94efacb690bf5521e3",
];

export const tranchedPoolResolvers: Resolvers[string] = {
  name(tranchedPool: TranchedPool) {
    return (
      POOL_METADATA[tranchedPool.id as keyof typeof POOL_METADATA]?.name ??
      `Pool ${tranchedPool.id}`
    );
  },
  borrowerName(tranchedPool: TranchedPool) {
    const borrowerId =
      POOL_METADATA[tranchedPool.id as keyof typeof POOL_METADATA]?.borrower;
    if (borrowerId) {
      const borrower =
        BORROWER_METADATA[borrowerId as keyof typeof BORROWER_METADATA];
      if (borrower) {
        return borrower.name;
      }
    }
    return "Borrower";
  },
  borrowerLogo(tranchedPool: TranchedPool) {
    const borrowerId =
      POOL_METADATA[tranchedPool.id as keyof typeof POOL_METADATA]?.borrower;
    if (borrowerId) {
      const borrower =
        BORROWER_METADATA[borrowerId as keyof typeof BORROWER_METADATA];
      if (borrower) {
        return borrower.logo;
      }
    }
    return null;
  },
  async collectedPaymentBalance(
    tranchedPool: TranchedPool
  ): Promise<BigNumber> {
    // Small optimization if tranchedPool.creditLine is already present in cache
    let creditLineAddress = tranchedPool?.creditLineAddress;
    if (creditLineAddress) {
      return collectedPaymentBalance(creditLineAddress);
    }
    const tranchedPoolContract = await getContract({
      name: "TranchedPool",
      address: tranchedPool.id,
    });
    creditLineAddress = await tranchedPoolContract.creditLine();
    return collectedPaymentBalance(creditLineAddress);
  },
  async delinquency(tranchedPool: TranchedPool): Promise<LoanDelinquency> {
    // alma 3. can take this out after they hit on-chain default
    if (tranchedPool.id === "0xefeb69edf6b6999b0e3f2fa856a2acf3bdea4ab5") {
      return "LATE";
    }
    const secondsPerDay = 60 * 60 * 24;
    const provider = getProvider();
    const tranchedPoolContract = await getContract({
      name: "TranchedPool",
      address: tranchedPool.id,
    });
    const goldfinchConfigContract = await getContract({
      name: "GoldfinchConfig",
      address: await tranchedPoolContract.config(),
    });
    const creditLineContract = await getContract({
      name: "CreditLine",
      address: await tranchedPoolContract.creditLine(),
    });
    const [currentBlock, gracePeriodInDays, lastFullPaymentTime] =
      await Promise.all([
        provider.getBlock("latest"),
        goldfinchConfigContract.getNumber(5),
        creditLineContract.lastFullPaymentTime(),
      ]);

    let isLate = false;
    if (oldCreditLines.includes(creditLineContract.address)) {
      const paymentPeriodInDays = 30; // This is lazy, but all old CreditLines that don't have `isLate()` implemented have 30 day payment periods
      isLate =
        currentBlock.timestamp >
        lastFullPaymentTime.toNumber() + paymentPeriodInDays * secondsPerDay;
    } else {
      // Cautious, isLate() isn't on all credit lines
      try {
        isLate = await creditLineContract.isLate();
      } catch (e) {
        const paymentPeriodInDays = 30; // This is lazy, but all old CreditLines that don't have `isLate()` implemented have 30 day payment periods
        isLate =
          currentBlock.timestamp >
          lastFullPaymentTime.toNumber() + paymentPeriodInDays * secondsPerDay;
      }
    }

    const gracePeriodInSeconds = gracePeriodInDays.toNumber() * secondsPerDay;
    if (!isLate) {
      return "CURRENT";
    } else if (
      currentBlock.timestamp <
      lastFullPaymentTime.toNumber() + gracePeriodInSeconds
    ) {
      return "GRACE_PERIOD";
    } else {
      return "LATE";
    }
  },
  async isAfterTermEndTime(tranchedPool: TranchedPool): Promise<boolean> {
    // Small optimization if tranchedPool.creditLine is already present in cache
    let creditLineAddress = tranchedPool?.creditLineAddress;
    if (creditLineAddress) {
      return isAfterTermEndTime(creditLineAddress);
    }
    const tranchedPoolContract = await getContract({
      name: "TranchedPool",
      address: tranchedPool.id,
    });
    creditLineAddress = await tranchedPoolContract.creditLine();
    return isAfterTermEndTime(creditLineAddress);
  },
  async interestOwed(tranchedPool: TranchedPool): Promise<BigNumber> {
    // Small optimization if tranchedPool.creditLine is already present in cache
    let creditLineAddress = tranchedPool?.creditLineAddress;
    if (creditLineAddress) {
      return interestOwed(creditLineAddress);
    }
    const tranchedPoolContract = await getContract({
      name: "TranchedPool",
      address: tranchedPool.id,
    });
    creditLineAddress = await tranchedPoolContract.creditLine();
    return interestOwed(creditLineAddress);
  },
};
